<template>
  <v-navigation-drawer v-model="drawer" :width="drawer ? '700' : undefined">
    <v-card-item>
      <v-card-title>Theme Customization</v-card-title>
      <v-card-subtitle>
        Pick a curated theme or choose your own colors.
      </v-card-subtitle>
    </v-card-item>

    <v-expansion-panels
      v-model="curatedThemesOpen"
      variant="accordion"
      class="pa-2"
      color="voting"
    >
      <v-expansion-panel value="curated-themes">
        <template #title>
          <v-list-item
            title="Curated Themes"
            subtitle="Choose from any of our ready-made palettes"
            density="compact"
            class="pa-0"
          />
        </template>
        <v-expansion-panel-text elevation="0" :class="`bg-voting`">
          <v-item-group
            :model-value="[selectedNamedTheme]"
            mandatory
            @update:model-value="selectedNamedTheme = $event as any"
          >
            <v-row>
              <v-col
                v-for="namedTheme in namedThemes"
                :key="namedTheme.name"
                cols="12"
                md="6"
              >
                <v-item
                  v-slot="{ isSelected, toggle }"
                  :value="namedTheme.name"
                >
                  <v-card
                    flat
                    :class="['rounded-t-0 ']"
                    color="surface"
                    :border="isSelected ? 'current md' : ''"
                    @click="toggle"
                  >
                    <div class="d-flex" style="height: 50px">
                      <div
                        v-for="colorName in themeColorNames"
                        :key="colorName"
                        :style="{
                          backgroundColor:
                            namedTheme.colors[colorName] || 'transparent',
                        }"
                        class="flex-grow-1 h-100 rounded-0"
                      ></div>
                    </div>
                    <v-card-text class="py-2">
                      {{ namedTheme.displayName }}
                    </v-card-text>
                  </v-card>
                </v-item>
              </v-col>
            </v-row>
          </v-item-group>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>

    <v-card-item class="mt-12">
      <v-card-title>
        Custom Colors
        <span v-if="customThemeLocked">🔒</span>
      </v-card-title>
      <v-card-subtitle>
        <span v-if="customThemeLocked">
          Upgrade to the {{ TierDisplayNames[TierName.ENTERPRISE] }} Tier to
          unlock.
        </span>
        <span v-else>Choose your own colors for your survey.&nbsp;</span>

        <a
          class="text-secondary d-inline-block"
          target="_blank"
          href="https://knowledge.opinionx.co/en/articles/11496810-survey-theme-customize-the-colors-on-your-opinionx-survey-to-match-your-brand"
        >
          Learn more
        </a>
      </v-card-subtitle>
    </v-card-item>

    <v-expansion-panels variant="accordion" class="pa-2">
      <v-expansion-panel
        v-for="{ name: color, title, subtitle } in surveyColors"
        :key="color"
        :color="color"
        :value="color"
      >
        <template #title>
          <v-list-item :title :subtitle density="compact" class="pa-0" />
        </template>
        <v-expansion-panel-text
          :class="`bg-${color}`"
          elevation="0"
          class="rounded-0"
        >
          <div class="d-flex flex-wrap">
            <v-card-text class="px-0">
              <v-card-subtitle class="text-capitalize ps-0">
                Primary Color
              </v-card-subtitle>
              <v-color-picker
                :disabled="customThemeLocked"
                :model-value="themeColors[color]"
                :bg-color="color"
                elevation="0"
                :modes="['hex']"
                @update:model-value="updateColor(color, $event)"
              />
            </v-card-text>
            <v-card-text class="px-0">
              <v-card-subtitle class="text-capitalize ps-0">
                Text color
              </v-card-subtitle>
              <v-color-picker
                :disabled="customThemeLocked"
                :model-value="themeColors[toTextColorName(color)]"
                elevation="0"
                :bg-color="color"
                :modes="['hex']"
                @update:model-value="
                  updateColor(toTextColorName(color), $event)
                "
              />
            </v-card-text>
          </div>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>

    <v-card-actions>
      <v-btn variant="text" color="on-surface" @click="resetTheme">
        reset to default
      </v-btn>
      <v-spacer></v-spacer>
      <v-btn variant="elevated" color="primary" @click="drawer = false">
        close
      </v-btn>
    </v-card-actions>
  </v-navigation-drawer>
</template>

<script setup lang="ts">
import { themeName as vuetifyThemeName } from '@/plugins/vuetify';
import { useSubmissionStore } from '@/stores/submission';
import { useSurveyStore } from '@/stores/survey';
import { useDebounceFn } from '@vueuse/core';
import { computed } from 'vue';
import { useTheme } from 'vuetify';
import { getForeground, parseColor } from 'vuetify/lib/util/colorUtils.mjs';
import { APCAcontrast } from 'vuetify/lib/util/color/APCA.js';
import {
  getNamedThemeColors,
  namedThemes,
  SurveyTheme,
  themeColorNames,
  ThemeColors,
  ThemeName,
  ThemeType,
} from '@/types/theme';
import { TierDisplayNames, TierName } from '@opinionx/tier';
import { ref } from 'vue';

const drawer = defineModel<boolean>();

const colorNames = ['primary', 'surface', 'voting'] as const;

type ColorName = (typeof colorNames)[number];
type TextColorName = `on-${ColorName}`;
const isColorName = (value: string): value is ColorName =>
  colorNames.includes(value as ColorName);

const toTextColorName = (color: ColorName): TextColorName => `on-${color}`;

const surveyColors = [
  {
    name: 'surface',
    title: 'Background ',
    subtitle: 'Choose a backdrop that fits your brand',
  },
  {
    name: 'primary',
    title: 'Navigation',
    subtitle:
      'Buttons like "Start" and "Next" for progressing through the survey',
  },
  {
    name: 'voting',
    title: 'Voting',
    subtitle:
      'Such as ranking components, "Skip" buttons, and Multiple Choice cards',
  },
] as const satisfies { title: string; subtitle: string; name: ColorName }[];

const surveyStore = useSurveyStore();
const submissionStore = useSubmissionStore();

const vuetifyTheme = useTheme();

const updateColor = (color: ColorName | TextColorName, newValue: string) => {
  if (isColorName(color)) {
    themeColors.value = {
      [color]: newValue,
      [toTextColorName(color)]: getTextColor(color, newValue),
    };
  } else {
    themeColors.value = {
      [color]: newValue,
    };
  }
};

const getTextColor = (color: ColorName, newValue: string) => {
  const current = themeColors.value[`on-${color}`];
  const contrast = Math.abs(
    APCAcontrast(parseColor(current as any), parseColor(newValue)),
  );

  return contrast < 50 ? getForeground(newValue) : current;
};

const themeColors = computed<ThemeColors>({
  get() {
    const computedTheme =
      vuetifyTheme.computedThemes.value[vuetifyThemeName.CUSTOM_SURVEY_THEME]!
        .colors;
    return {
      primary: computedTheme.primary,
      surface: computedTheme.surface,
      voting: computedTheme.voting,
      'on-primary': computedTheme['on-primary'],
      'on-surface': computedTheme['on-surface'],
      'on-voting': computedTheme['on-voting'],
    };
  },
  set(theme: ThemeColors) {
    const colors = { ...themeColors.value, ...theme };
    updateSurveyTheme({ ...colors, type: ThemeType.CUSTOM }, colors);
  },
});

const updateSurveyTheme = (theme: SurveyTheme, colors: ThemeColors) => {
  for (const colorName of themeColorNames) {
    const colorValue = colors[colorName];
    if (!colorValue) {
      continue;
    }
    vuetifyTheme.themes.value[vuetifyThemeName.CUSTOM_SURVEY_THEME]!.colors[
      colorName
    ] = colorValue;
  }
  surveyStore.theme = theme;
  debouncedUpdate(theme);
};

const debouncedUpdate = useDebounceFn(async (theme: SurveyTheme) => {
  await surveyStore.updateTheme(theme);
  submissionStore.updateTheme(surveyStore.theme);
}, 500);

const resetTheme = async () => {
  await surveyStore.updateTheme(null);
  submissionStore.updateTheme(surveyStore.theme);
};

const customThemeLocked = computed(() => {
  return !surveyStore.isEnterprise;
});

const selectedNamedTheme = computed<ThemeName | null>({
  get: () => {
    if (!surveyStore.theme) {
      return 'opinionx-default';
    }
    return surveyStore.theme?.type === ThemeType.NAMED
      ? surveyStore.theme.name
      : null;
  },
  set: (themeName: ThemeName | null) => {
    if (!themeName) {
      return;
    }
    const themeColors = getNamedThemeColors(themeName);
    updateSurveyTheme({ name: themeName, type: ThemeType.NAMED }, themeColors);
  },
});

const curatedThemesOpen = ref(
  selectedNamedTheme.value ? 'curated-themes' : null,
);
</script>

<style scoped>
:deep(.v-color-picker-edit__input > span),
:deep(.v-color-picker-preview__dot) {
  display: none;
}

:deep(.v-color-picker-preview__sliders) {
  padding-inline: 0;
}

:deep(.v-color-picker-preview__eye-dropper > button),
:deep(.v-color-picker-edit__input > input) {
  color: currentColor !important;
}
</style>
